import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock, X } from "lucide-react";

const STORAGE_KEY = "magee_book_call_dismissed_v1";
const SHOW_THRESHOLD = 640; // px scrolled past hero
const HIDE_ON_ROUTES = ["/admin", "/contact"];

export default function FloatingBookCall() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") setDismissed(true);
    } catch (e) {
      // sessionStorage unavailable — treat as not dismissed
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_THRESHOLD);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const routeHidden = HIDE_ON_ROUTES.some((p) => location.pathname.startsWith(p));
  const show = visible && !dismissed && !routeHidden;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (err) {
      // ignore
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-40 bottom-5 right-5 md:bottom-7 md:right-7 pointer-events-auto"
          data-testid="floating-book-call"
        >
          <Link
            to="/contact#book"
            aria-label="Book a 15-minute consultation call"
            data-testid="floating-book-call-link"
            className="group relative inline-flex items-center gap-2.5 pr-11 pl-4 md:pl-5 py-3 md:py-3.5 rounded-full bg-white/95 backdrop-blur-md border border-[var(--border)] shadow-[0_18px_40px_-16px_rgba(11,37,69,0.35)] hover:shadow-[0_20px_50px_-14px_rgba(6,182,212,0.45)] hover:border-[var(--accent)]/50 transition-all duration-500"
          >
            <span
              aria-hidden="true"
              className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center shadow-[0_6px_16px_-4px_rgba(6,182,212,0.6)]"
            >
              <CalendarClock size={14} />
            </span>
            <span className="font-editorial text-[13px] md:text-[14px] font-medium text-[var(--text)] leading-tight">
              Chat with Service Team &mdash; 15 min
            </span>

            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss booking prompt"
              data-testid="floating-book-call-dismiss"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition-colors"
            >
              <X size={12} />
            </button>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
